import { useState, useRef, useCallback, useEffect } from 'react'
import {Phone, Calendar} from 'lucide-react'
import { useNavigate } from 'react-router-dom'

export interface BeforeAfterSliderProps {
  /** URL of the "before" image */
  beforeImage: string
  /** URL of the "after" image */
  afterImage: string
  /** Alt text for the before image */
  beforeAlt?: string
  /** Alt text for the after image */
  afterAlt?: string
  /** Short label shown on the before side */
  beforeLabel?: string
  /** Short label shown on the after side */
  afterLabel?: string
  /** Project title displayed below the slider */
  projectTitle?: string
  /** Service badge (e.g. "Full Front PPF") */
  serviceBadge?: string
  /** Optional quote call-to-action below the slider */
  showCta?: boolean
  /** Optional extra CSS classes on the outer wrapper */
  className?: string
  /** Initial handle position as percentage (0–100), default 50 */
  initialPosition?: number
}

/**
 * BeforeAfterSlider
 *
 * A drag-to-reveal before/after comparison widget. Pure CSS + React pointer
 * events — no external library. Handles mouse, touch, and keyboard.
 *
 * Designed for Mr. Wrap / San Diego PPF Landing Page dark automotive aesthetic.
 * Drop-in anywhere with a single JSX line.
 */
const BeforeAfterSlider = ({
  beforeImage,
  afterImage,
  beforeAlt = 'Before',
  afterAlt = 'After',
  beforeLabel = 'Before',
  afterLabel = 'After',
  projectTitle,
  serviceBadge,
  showCta = false,
  className = '',
  initialPosition = 50,
}: BeforeAfterSliderProps) => {
  const [position, setPosition] = useState(initialPosition)
  const [isDragging, setIsDragging] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // ─── Calculation helper ──────────────────────────────────────────────────
  const calcPosition = useCallback((clientX: number): number => {
    const el = containerRef.current
    if (!el) return 50
    const rect = el.getBoundingClientRect()
    const x = clientX - rect.left
    return Math.min(100, Math.max(0, (x / rect.width) * 100))
  }, [])

  // ─── Mouse events ────────────────────────────────────────────────────────
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  // ─── Touch events ────────────────────────────────────────────────────────
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true)
    setPosition(calcPosition(e.touches[0].clientX))
  }

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging) return
      e.preventDefault()
      setPosition(calcPosition(e.touches[0].clientX))
    },
    [isDragging, calcPosition]
  )

  const handleTouchEnd = useCallback(() => setIsDragging(false), [])

  // ─── Global move/up for smooth dragging outside the element ─────────────
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return
      setPosition(calcPosition(e.clientX))
    }
    const onMouseUp = () => setIsDragging(false)

    if (isDragging) {
      window.addEventListener('mousemove', onMouseMove)
      window.addEventListener('mouseup', onMouseUp)
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [isDragging, calcPosition])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    el.addEventListener('touchmove', handleTouchMove, { passive: false })
    el.addEventListener('touchend', handleTouchEnd)
    return () => {
      el.removeEventListener('touchmove', handleTouchMove)
      el.removeEventListener('touchend', handleTouchEnd)
    }
  }, [handleTouchMove, handleTouchEnd])

  // ─── Keyboard accessibility ──────────────────────────────────────────────
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') setPosition(p => Math.max(0, p - 2))
    if (e.key === 'ArrowRight') setPosition(p => Math.min(100, p + 2))
  }

  // ─── Placeholder gradient (shown when images are swapped in) ─────────────
  const isPlaceholderBefore = beforeImage.startsWith('placeholder:')
  const isPlaceholderAfter = afterImage.startsWith('placeholder:')

  return (
    <div className={`w-full ${className}`}>
      {/* Slider container */}
      <div
        ref={containerRef}
        className="relative w-full overflow-hidden rounded-2xl select-none cursor-ew-resize"
        style={{ aspectRatio: '16/9', touchAction: 'none' }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        role="slider"
        aria-label={`Before and after comparison: ${beforeLabel} vs ${afterLabel}`}
        aria-valuenow={Math.round(position)}
        aria-valuemin={0}
        aria-valuemax={100}
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        {/* ── AFTER image (full width, bottom layer) ── */}
        {isPlaceholderAfter ? (
          <div
            className="absolute inset-0 w-full h-full flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #0e7490 0%, #164e63 100%)' }}
          >
            <div className="text-center text-white/60 px-8">
              <div className="text-5xl mb-3 opacity-40">✦</div>
              <p className="text-lg font-semibold opacity-60">After</p>
            </div>
          </div>
        ) : (
          <img
            src={afterImage}
            alt={afterAlt}
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />
        )}

        {/* ── BEFORE image (clipped to left of handle) ── */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ width: `${position}%` }}
        >
          {isPlaceholderBefore ? (
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                width: containerRef.current
                  ? `${containerRef.current.getBoundingClientRect().width}px`
                  : '100vw',
                background: 'linear-gradient(135deg, #1c1917 0%, #374151 100%)',
              }}
            >
              <div className="text-center text-white/60 px-8">
                <div className="text-5xl mb-3 opacity-40">◇</div>
                <p className="text-lg font-semibold opacity-60">Before</p>
              </div>
            </div>
          ) : (
            <img
              src={beforeImage}
              alt={beforeAlt}
              className="absolute inset-0 h-full object-cover"
              style={{ width: containerRef.current ? `${containerRef.current.getBoundingClientRect().width}px` : '100vw' }}
              draggable={false}
            />
          )}
        </div>

        {/* ── Divider line ── */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg"
          style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
        />

        {/* ── Handle grip ── */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 flex items-center justify-center z-10"
          style={{ left: `${position}%` }}
        >
          <div
            className={`w-12 h-12 rounded-full bg-cyan-500 border-4 border-white shadow-2xl flex items-center justify-center transition-transform ${
              isDragging ? 'scale-110' : 'scale-100'
            }`}
            style={{ boxShadow: '0 0 0 4px rgba(6,182,212,0.4), 0 4px 20px rgba(0,0,0,0.5)' }}
          >
            {/* Left-right arrows */}
            <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 7H19M1 7L5 3M1 7L5 11M19 7L15 3M19 7L15 11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

        {/* ── Before / After labels ── */}
        <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full border border-white/20 pointer-events-none select-none">
          {beforeLabel}
        </div>
        <div className="absolute top-3 right-3 bg-cyan-500/90 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full border border-cyan-300/30 pointer-events-none select-none">
          {afterLabel}
        </div>

        {/* ── Drag hint — fades after first interaction ── */}
        {!isDragging && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 pointer-events-none select-none">
            <div className="bg-black/60 backdrop-blur-sm text-white/70 text-xs px-3 py-1 rounded-full flex items-center gap-1.5">
              <svg width="12" height="12" viewBox="0 0 20 14" fill="none">
                <path d="M1 7H19M1 7L5 3M1 7L5 11M19 7L15 3M19 7L15 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Drag to compare
            </div>
          </div>
        )}
      </div>

      {/* ── Caption row ── */}
      {(projectTitle || serviceBadge) && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mt-4 px-1">
          {projectTitle && (
            <p className="text-white font-semibold text-sm leading-snug">{projectTitle}</p>
          )}
          {serviceBadge && (
            <span className="inline-block bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-semibold px-3 py-1 rounded-full flex-shrink-0">
              {serviceBadge}
            </span>
          )}
        </div>
      )}

      {/* ── Optional inline CTA ── */}
      {showCta && (
        <div className="flex gap-3 mt-5 flex-wrap">
          <a
            href="tel:858-282-8119"
            className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition"
          >
            <Phone size={16} />
            Call 858-282-8119
          </a>
          <button
            onClick={() => { navigate('/contact'); setTimeout(() => window.scrollTo(0, 0), 100) }}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition"
          >
            <Calendar size={16} />
            Get a Free Quote
          </button>
        </div>
      )}
    </div>
  )
}

export default BeforeAfterSlider
